import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check } from "lucide-react";
import { BUSINESS_TYPES } from "../lib/industry";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ salon_name: "", name: "", email: "", phone: "", password: "", business_type: "hair" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to Booking Babe! Your 14-day trial has started.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-center bg-primary text-primary-foreground p-16">
        <Link to="/" className="flex items-center gap-2 mb-12">
          <img src="/bookingbabe-logo.png" alt="Booking Babe" className="w-8 h-8 rounded-md object-cover" />
          <span className="font-display text-2xl font-extrabold tracking-tight text-white">Booking Babe</span>
        </Link>
        <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Your beauty business command center, ready in 2 minutes.</h2>
        <ul className="mt-10 space-y-4">
          {["14-day free trial, no card required", "Your own public booking page", "Automated reminders & follow-ups", "Collect deposits to stop no-shows"].map((t) => (
            <li key={t} className="flex items-center gap-3 text-primary-foreground/90">
              <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center"><Check size={14} /></span>{t}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/bookingbabe-logo.png" alt="Booking Babe" className="w-7 h-7 rounded-md object-cover" />
            <span className="font-display text-xl font-extrabold tracking-tight">Booking Babe</span>
          </Link>
          <h1 className="font-display text-2xl font-bold mb-1">Create your account</h1>
          <p className="text-muted-foreground text-sm mb-6">Start your free trial today.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="salon">Business name</Label>
              <Input id="salon" data-testid="signup-salon" value={form.salon_name} onChange={set("salon_name")} required className="mt-1.5" placeholder="Glow Beauty Studio" />
            </div>
            <div>
              <Label htmlFor="business_type">Business type</Label>
              <select id="business_type" data-testid="signup-business-type" value={form.business_type} onChange={set("business_type")}
                className="mt-1.5 w-full h-10 rounded-md border border-input bg-card px-3 text-sm">
                {BUSINESS_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1">We'll set up starter services and wording to match.</p>
            </div>
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="signup-name" value={form.name} onChange={set("name")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="signup-email" type="email" value={form.email} onChange={set("email")} required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" data-testid="signup-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="signup-password" type="password" value={form.password} onChange={set("password")} required className="mt-1.5" />
            </div>
            <Button type="submit" data-testid="signup-submit" disabled={loading} className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
              {loading ? "Creating…" : "Start free trial"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
